
// The sentence for the signals line (plan 444 M4, design D8), shared by the
// buyer surfaces and the pro's signals mirror so both read the same words. The
// server owns every number here: a measured field arrives only when the agent
// has earned it, already past its floor, and this module only FORMATS what is
// present. It holds no thresholds, does no arithmetic on a value, and never
// invents a segment for an absent field. That is pinned by signals-copy.test.ts,
// which reads this file's source.

/**
 * The display-only signals block (plan 444 M4). Every measured field is absent
 * when the agent has not earned it; consumers only format what is present and
 * never derive a count, band, or year of their own.
 */
export interface AgentSignals {
  closed_on_sill?: number;
  responsiveness_band?: "within_hours" | "within_day";
  licensed_years?: number;
  /** YYYY-MM. */
  joined_month?: string;
}

export interface SignalSegment {
  key: "licensed" | "closed" | "responsiveness";
  text: string;
}

export type SignalsLine =
  | { kind: "segments"; segments: SignalSegment[] }
  | { kind: "joined"; text: string };

const YEARS = new Intl.PluralRules("en");

const RESPONSIVENESS_COPY: Record<
  NonNullable<AgentSignals["responsiveness_band"]>,
  string
> = {
  within_hours: "Responds within hours",
  within_day: "Responds within a day",
};

const MONTH_NAMES: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

function joinedText(joinedMonth: string | undefined): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(joinedMonth ?? "");
  if (!match) return null;
  const [, year, month] = match;
  const name = month ? MONTH_NAMES[month] : undefined;
  return name ? `Joined Sill ${name} ${year}` : null;
}

/**
 * The line to render, or null when there is nothing honest to say. Measured
 * and licensed segments win; the joined month is the fallback only when none
 * of them is present.
 */
export function signalsLine(
  signals: AgentSignals | null | undefined,
): SignalsLine | null {
  if (!signals) return null;
  const segments: SignalSegment[] = [];
  if (signals.licensed_years != null) {
    const unit = YEARS.select(signals.licensed_years) === "one" ? "yr" : "yrs";
    segments.push({
      key: "licensed",
      text: `Licensed ${signals.licensed_years} ${unit}`,
    });
  }
  if (signals.closed_on_sill != null) {
    segments.push({
      key: "closed",
      text: `${signals.closed_on_sill} closed on Sill`,
    });
  }
  if (signals.responsiveness_band != null) {
    const text = RESPONSIVENESS_COPY[signals.responsiveness_band];
    if (text) {
      segments.push({ key: "responsiveness", text });
    }
  }
  if (segments.length) return { kind: "segments", segments };
  const joined = joinedText(signals.joined_month);
  return joined ? { kind: "joined", text: joined } : null;
}

/**
 * The line flattened to one plain-text sentence, or null when there is nothing
 * honest to say. Both the buyer surfaces and the pro mirror render this.
 */
export function signalsLineText(
  signals: AgentSignals | null | undefined,
): string | null {
  const line = signalsLine(signals);
  if (!line) return null;
  return line.kind === "joined"
    ? line.text
    : line.segments.map((segment) => segment.text).join(" · ");
}
